/* eslint-disable */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { getHigherUnitAndQuantity, getUnitConvertedQuantity, getUnitConvertedUnit, handleResponseStreamDownload } from '@/Helpers';
import { Add, Remove } from '@mui/icons-material';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices } from '@/Services';
import { modalActions } from '@/Actions';
import { CustomModal } from '@/Components/Modals/CustomModal';

const getReference = (row) => {
	if (row?.refId) return `${row?.refType ?? ''} ID: ${row.refId}`.trim();
	if (row?.orderId) return `Dispatch Order ID: ${row.orderId}`;
	if (row?.poId) return `Work Order ID: ${row.poId}`;
	if (row?.grnId) return `GRN ID: ${row.grnId}`;
	if (row?.auditId) return `Audit ID: ${row.auditId}`;
	return '-';
};

const getRowBg = (movementType) => {
	if (movementType === 'Stocked In') return 'bg-[#00ff0d1a]';
	if (movementType === 'Stocked Out') return 'bg-[#ff0c0c1a]';
	return '';
};

const BatchDispatchOrders = ({ batchId, itemDetails, dateRange }) => {
	const [loading, setLoading] = useState(true);
	const [orders, setOrders] = useState([]);
	const unit = itemDetails?.unit ?? 'gram';

	const fmtQty = (value) => {
		if (value == null) return '-';
		const [convertedValue, convertedUnit] = getHigherUnitAndQuantity(unit, value);
		return `${convertedValue} ${convertedUnit}`;
	};

	useEffect(() => {
		let active = true;
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await inventoryAnalyticsServices.batchwiseDispatchInfo({
					data: {
						batchIds: [batchId]
					},
				});
				const rows = response?.statusCode === 200 ? (response?.data ?? []) : [];
				if (active) setOrders(rows?.length ? rows : []);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('error', error);
				if (active) setOrders([]);
			} finally {
				if (active) setLoading(false);
			}
		};
		fetchData();
		return () => {
			active = false;
		};
	}, [batchId]);

	if (loading) {
		return (
			<div className='flex items-center justify-center gap-2 py-10 text-sm text-gray-500'>
				<Loader2 className='w-4 h-4 animate-spin' />
				Loading dispatch orders...
			</div>
		);
	}

	if (!orders?.length) {
		return (
			<div className='py-10 text-center'>
				<p className='text-sm font-medium text-gray-500'>No dispatch orders found</p>
				<p className='mt-1 text-xs text-gray-400'>
					This batch has not been dispatched to any order for the selected period.
				</p>
			</div>
		);
	}

	return (
		<div className='max-h-[55vh] overflow-auto border border-gray-200 rounded-lg'>
			<table className='w-full text-xs text-left'>
				<thead>
					<tr className='sticky top-0 bg-gray-50 text-[10px] font-semibold tracking-wide text-gray-500 uppercase'>
						<th className='px-3 py-2 text-left'>Client Po Id</th>
						<th className='px-3 py-2 text-left'>Dispatch Date</th>
						<th className='px-3 py-2 text-left'>Dispatched Qty</th>
					</tr>
				</thead>
				<tbody className='divide-y divide-gray-100'>
					{orders.map((o, idx) => (
						<tr key={o.orderId ?? o.id ?? idx} className='hover:bg-gray-50'>
							<td className='px-3 py-2 font-semibold text-left text-gray-800'>
								{o?.clientPoId}
							</td>
							<td className='px-3 py-2 text-left text-gray-600'>
								{o?.dispatchDate
									? moment(o.dispatchDate).format('DD-MM-YYYY HH:mm')
									: '-'}
							</td>
							<td className='px-3 py-2 font-semibold text-left text-amber-600'>
								{fmtQty(o.qty)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const MovementHistory = ({ batchId, itemDetails, dateRange }) => {
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState([]);
	const unit = itemDetails?.unit ?? 'gram';

	useEffect(() => {
		let active = true;
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await inventoryAnalyticsServices.scanWiseSkuTrail({
					data: {
						variantId: itemDetails?.variantId,
						productId: itemDetails?.id,
						storeId: itemDetails?.storeId,
						batchId,
						dateRangeFilter: {
							from: moment(dateRange?.from).format('YYYY-MM-DD'),
							to: moment(dateRange?.to).format('YYYY-MM-DD'),
						},
					},
				});
				if (active) {
					setRows(response?.responseCode === 'SS-001' ? (response?.data ?? []) : []);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('error', error);
				if (active) setRows([]);
			} finally {
				if (active) setLoading(false);
			}
		};
		fetchData();
		return () => {
			active = false;
		};
	}, [batchId]);

	if (loading) {
		return (
			<div className='flex items-center justify-center gap-2 py-8 text-sm text-gray-500'>
				<Loader2 className='w-4 h-4 animate-spin' />
				Loading movement history...
			</div>
		);
	}

	if (!rows?.length) {
		return (
			<div className='px-6 py-8 text-center'>
				<p className='text-sm font-medium text-gray-500'>No movement history yet</p>
				<p className='mt-1 text-xs text-gray-400'>
					This batch has no recorded activity for the selected period.
				</p>
			</div>
		);
	}

	const unitLabel = `${getUnitConvertedUnit(1, unit, true)}`;

	return (
		<div className='px-6 py-4'>
			<p className='mb-2 text-xs font-semibold tracking-wide text-gray-400 uppercase'>
				Movement History
			</p>
			<div className='overflow-x-auto bg-white border border-gray-200 rounded-lg'>
				<table className='w-full text-[11px]'>
					<thead>
						<tr className='bg-gray-50 text-[10px] font-semibold tracking-wide text-gray-500 uppercase'>
							<th className='px-2 py-1.5 text-left'>QRCode</th>
							<th className='px-2 py-1.5 text-left'>Stock Id</th>
							<th className='px-2 py-1.5 text-left'>Unit</th>
							<th className='px-2 py-1.5 text-left'>Ref. ID</th>
							<th className='px-2 py-1.5 text-left'>Movement Type</th>
							<th className='px-2 py-1.5 text-left'>Activity Date</th>
							<th className='px-2 py-1.5 text-left'>Quantity</th>
							<th className='px-2 py-1.5 text-left'>Post Update Qty.</th>
							<th className='px-2 py-1.5 text-left'>Is Stock Updated</th>
							<th className='px-2 py-1.5 text-left'>Activity By</th>
							<th className='px-2 py-1.5 text-left'>Reason</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{rows.map((row, idx) => {
							const isIn = row?.movementType === 'Stocked In';
							return (
								<tr key={row?.id ?? idx} className={getRowBg(row?.movementType)}>
									<td className='px-2 py-1.5'>
										{row?.qrCode ? (
											<a
												className='text-blue-500 underline'
												href={`/console2/order/tracking?type=qrCode&value=${row.qrCode}`}
												target='_blank'
												rel='noreferrer'
											>
												{row.qrCode}
											</a>
										) : (
											'-'
										)}
									</td>
									<td className='px-2 py-1.5 text-gray-700'>{row?.stockId ?? '-'}</td>
									<td className='px-2 py-1.5 text-gray-700'>{unitLabel}</td>
									<td className='px-2 py-1.5 text-gray-700'>{getReference(row)}</td>
									<td className='px-2 py-1.5 font-medium text-gray-800 whitespace-nowrap'>
										{row?.movementType ?? '-'}
									</td>
									<td className='px-2 py-1.5 text-gray-700 whitespace-nowrap'>
										{row?.activityDate
											? moment(row.activityDate).format('YYYY-MM-DD hh:mm A')
											: '-'}
									</td>
									<td className='px-2 py-1.5'>
										<div className='flex items-center gap-1 font-semibold'>
											{isIn ? (
												<Add className='text-green-900' sx={{ fontSize: 14 }} />
											) : (
												<Remove className='text-red-800' sx={{ fontSize: 14 }} />
											)}
											{getUnitConvertedQuantity(row?.qty, unit, true, true, true)}
										</div>
									</td>
									<td className='px-2 py-1.5 text-gray-700'>
										{row?.postUpdateQty == null
											? '-'
											: getUnitConvertedQuantity(row.postUpdateQty, unit, true, true, true)}
									</td>
									<td className='px-2 py-1.5 text-gray-700'>
										{row?.stockUpdated === false ? 'No' : 'Yes'}
									</td>
									<td className='px-2 py-1.5 text-gray-700'>{row?.activityBy ?? '-'}</td>
									<td className='px-2 py-1.5 text-gray-700'>{row?.reason ?? '-'}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const BatchWiseTable = (props) => {
	const { data = [], itemDetails, dateRange, allAvailable = false } = props;
	const dispatch = useDispatch();
	const [exporting, setExporting] = useState(false);
	const unit = itemDetails?.unit ?? '';
	const fmtQty = (value) => {
		if (value == null) return '-';
		const [convertedValue, convertedUnit] = getHigherUnitAndQuantity(unit, value);
		return `${convertedValue} ${convertedUnit}`;
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const from = moment(dateRange?.from).format('YYYY-MM-DD');
			const to = moment(dateRange?.to).format('YYYY-MM-DD');
			const response = await inventoryAnalyticsServices.batchWiseExport({
				data: {
					variantId: itemDetails?.variantId,
					...(allAvailable && { allBatches: true }),
					dateRangeFilter: { from, to },
				},
			});
			if (response?.ok) {
				const fileName = allAvailable
					? `BatchWise_AllAvailable_${itemDetails?.variantId ?? ''}.csv`
					: `BatchWise_${itemDetails?.variantId ?? ''}_${from}_${to}.csv`;
				await handleResponseStreamDownload(response, fileName);
				notificationServices.generateNotification({
					type: 'success',
					message: 'Exported successfully',
				});
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Some error occurred while exporting',
				});
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occurred while exporting',
			});
		} finally {
			setExporting(false);
		}
	};

	const openDispatchOrders = (row) => {
		dispatch(
			modalActions.toggleCustomModal(true, {
				header: `Dispatch Orders${row?.batchName ? ` — ${row.batchName}` : ''}`,
				subtitle: 'Orders this batch was dispatched with quantities',
				width: '34rem',
				height: 'auto',
				hideFooter: true,
				bodyContent: (
					<BatchDispatchOrders
						batchId={row?.batchId ?? row?.id}
						itemDetails={itemDetails}
						dateRange={dateRange}
					/>
				),
			})
		);
	};

	const columns = [
		{ key: 'batchName', title: 'BATCH NAME', width: 120 },
		{ key: 'mfgExpiry', title: 'MFG / EXPIRY', width: 100 },
		{ key: 'planned', title: 'PLANNED', width: 100 },
		{ key: 'produced', title: 'PRODUCED', width: 100 },
		{ key: 'dispatched', title: 'DISPATCHED', width: 100 },
		{ key: 'wastage', title: 'WASTAGE', width: 100 },
		{ key: 'expired', title: 'EXPIRED', width: 100 },
		{ key: 'available', title: 'AVAILABLE', width: 100 },
	];

	const customRowData = {
		batchName: (row) => (
			<span className='font-semibold text-gray-800'>
				{row?.batchName ?? '-'}
			</span>
		),
		mfgExpiry: (row) => {
			const mfg = row?.manufacturedDate;
			const exp = row?.expiryDate;
			return (
				<div className='flex flex-col text-xs text-gray-600'>
					<span>Mfg: {mfg ? moment(mfg).format('DD-MM-YYYY') : '-'}</span>
					<span>Exp: {exp ? moment(exp).format('DD-MM-YYYY') : '-'}</span>
				</div>
			);
		},
		planned: (row) => (
			<span className='font-semibold'>{fmtQty(row?.qty)}</span>
		),
		produced: (row) => (
			<span className='font-semibold'>{fmtQty(row?.grnFulfilled)}</span>
		),
		dispatched: (row) => {
			const hasDispatch = Number(row?.consumed) > 0;
			if (!hasDispatch) {
				return <span className='font-semibold text-amber-600'>{fmtQty(row?.consumed)}</span>;
			}
			return (
				<button
					type='button'
					onClick={(e) => {
						e.stopPropagation();
						openDispatchOrders(row);
					}}
					className='font-semibold text-amber-600 underline underline-offset-2 hover:text-amber-700'
				>
					{fmtQty(row?.consumed)}
				</button>
			);
		},
		wastage: (row) => (
			<span className='font-semibold text-orange-600'>{fmtQty(row?.wastage)}</span>
		),
		expired: (row) => (
			<span className='font-semibold text-rose-600'>{fmtQty(row?.expired)}</span>
		),
		available: (row) => (
			<span className='font-semibold text-green-600'>{fmtQty(row?.available)}</span>
		),
	};

	const renderSubRow = (row) => (
		<MovementHistory
			batchId={row?.batchId ?? row?.id}
			itemDetails={itemDetails}
			dateRange={dateRange}
		/>
	);

	return (
		<div className='w-full'>
			<div className='flex justify-end mb-2'>
				<button
					type='button'
					onClick={handleExport}
					disabled={exporting || !data?.length}
					className='inline-flex items-center gap-2 rounded-full bg-[#1d7044] px-5 py-2 text-sm font-semibold tracking-wide text-white shadow-sm transition-colors hover:bg-[#185c38] disabled:cursor-not-allowed disabled:opacity-50'
				>
					{exporting ? (
						<Loader2 size={16} className='animate-spin' />
					) : (
						<FileSpreadsheet size={16} />
					)}
					EXPORT
				</button>
			</div>
			<VariantTable
				data={data}
				columns={columns}
				customRowData={customRowData}
				headerCellClassName='bg-gray-50'
				headerRowClassName='bg-gray-50'
				customInfoLine='No batch data available'
				showSubRow
				renderSubRow={renderSubRow}
			/>
			<CustomModal />
		</div>
	);
};

export default BatchWiseTable;
